package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.LChevronUpIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t

/**
 * What the string is for, above the string itself.
 *
 * The one sentence that stops a mistranslation used to be readable only inside the Details panel —
 * `row.key.context` was read in exactly one place and rendered there — and opening that panel
 * unmounts the queue, so the cost of checking why a string exists was losing your place in the
 * list. A sentence that expensive is a sentence nobody reads, and the mistranslation happens.
 *
 * Truncated by the browser rather than by a character count. The spec leaves the character budget
 * open; it does not need answering, because the constraint is the width of the writing column and
 * `text-overflow` measures that in pixels at the size the text actually renders at. Expanding is in
 * place: this must never become a second surface to open.
 *
 * Nothing is drawn when no context is authored. The absence of a *budget* is information — it is
 * the prompt to set one — but the absence of context is only the ordinary case, and a permanent
 * "no context provided" above every source string is a line of chrome that teaches nobody anything.
 *
 * Deliberately silent about machine provenance: the identity row directly above already carries the
 * `MachineAttribution` badge, and one badge saying so is the whole of that contract.
 */
@Composable
internal fun EditorContextStrip(row: KeyRow) {
    val context = row.key.context?.takeIf(String::isNotBlank) ?: return
    var expanded by remember(row.key.id) { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    Row(
        Modifier
            .fillMaxWidth()
            .gap(Space.SM)
            .attr("data-editor-context-strip", if (expanded) "expanded" else "collapsed")
            .attr("role", "note")
            .attr("aria-label", t("editor.editor_context_strip.context", "Context")),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Div(EditorEyebrowStyle.toModifier().toAttrs()) { Text(t("editor.editor_context_strip.context", "Context")) }
        Div(
            Modifier
                .flexGrow(1)
                .minWidth(0.px)
                .color(palette.textSecondary)
                .fontSize(TypeScale.Small)
                .lineHeight(1.5)
                .styleModifier {
                    if (expanded) {
                        property("white-space", "pre-wrap")
                        property("overflow-wrap", "anywhere")
                    } else {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                }
                .toAttrs(),
        ) {
            Text(context)
        }
        // **A chevron, not two words.** The strip is one line above the source string, and a
        // "Show all" / "Show less" button beside it was the widest thing in the row — a control
        // whose label restated what the caret already says, taking room from the sentence it was
        // there to reveal. `LIconAction` keeps the promise an icon-only control has to make: the
        // name is spoken and shown, in one place, so the tooltip and the accessible name cannot
        // disagree the way the old `label`/`accessibleLabel` pair could.
        LIconAction(
            label = if (expanded) {
                t("editor.editor_context_strip.show_less_context", "Show less context")
            } else {
                t("editor.editor_context_strip.show_all_of_the_context", "Show all of the context")
            },
            onClick = { expanded = !expanded },
            modifier = Modifier
                .attr("aria-expanded", expanded.toString())
                .then(EditorCoarseTargetStyle.toModifier())
                .attr("data-editor-coarse-target", "tool")
                .attr("data-editor-context-disclosure", if (expanded) "expanded" else "collapsed"),
            size = LButtonSize.SM,
        ) {
            if (expanded) LChevronUpIcon() else LChevronDownIcon()
        }
    }
}
